import React from "react";
import PropTypes from "prop-types";
import BookStore from "../../stores/book-store";
import Dispatcher from "../../dispatcher";
import MenuHeader from "../ui_elements/menu-header";
import { Button } from "@vitalsource/vstui";
import ButtonGroup from "../ui_elements/button-group";
import classnames from "classnames";
import Isvg from "../../../vendor/react-inlinesvg";

const controls = {
  fonts: [
    { key: "default", value: "default" },
    { key: "sans-serif", value: "--RS__sansTf" },
    { key: "old-style", value: "--RS__oldStyleTf" },
    { key: "modern", value: "--RS__modernTf" },
    { key: "humanist", value: "--RS__humanistTf" },
    { key: "monospace", value: "--RS__monospaceTf" },
    { key: "open-dyslexic", value: "--RS__openDyslexicTf" },
    { key: "hyperlegible", value: "--RS__hyperlegibleTf" }
  ],
  textSizes: [
    { key: "small", buttonFontSize: 12, value: 1 },
    { key: "normal", buttonFontSize: 16, value: 1.5 },
    { key: "large", buttonFontSize: 19, value: 2 },
    { key: "extra-large", buttonFontSize: 22, value: 4 }
  ],
  lineHeightSizes: [
    { key: "small", value: 1 },
    { key: "medium", value: 1.5 },
    { key: "large", value: 2 }
  ],
  marginSizes: [
    { key: "small", value: 1 },
    { key: "medium", value: 2.5 },
    { key: "large", value: 5 }
  ],
  modes: ["day", "night", "sepia", "cyan"]
};

const controlDefaults = {
  enabled: false,
  font: { value: controls.fonts[0].value, enabled: false },
  lineHeight: { value: controls.lineHeightSizes[0].value, enabled: false },
  margin: { value: controls.marginSizes[0].value, enabled: false },
  mode: { value: controls.modes[0], enabled: false },
  textSize: { value: controls.textSizes[0].value, enabled: false }
};

const cc_default_config_0 = {
  enabled: true,
  font: { value: controls.fonts[4].value, enabled: true },
  lineHeight: { value: controls.lineHeightSizes[1].value, enabled: true },
  margin: { value: controls.marginSizes[0].value, enabled: true },
  mode: { value: controls.modes[0], enabled: true },
  textSize: { value: controls.textSizes[1].value, enabled: true }
};

class EnhancedFormattingMenu extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { ...this.loadContentSettings(props.isbn) };

    this._setFont = this._setFont.bind(this);
    this._setMode = this._setMode.bind(this);
    this._setLineHeight = this._setLineHeight.bind(this);
    this._setPageMargin = this._setPageMargin.bind(this);
    this._setTextSize = this._setTextSize.bind(this);
    this._pageReady = this._pageReady.bind(this);
    this._resetFormatting = this._resetFormatting.bind(this);
    this.saveContentSettings = this.saveContentSettings.bind(this);
    this.loadContentSettings = this.loadContentSettings.bind(this);
    this.applySettings = this.applySettings.bind(this);
  }

  componentDidMount() {
    Dispatcher.bind(Dispatcher.BOOK_PAGE_READY, this._pageReady);
    this.applySettings();
  }

  componentWillUnmount() {
    Dispatcher.unbind(Dispatcher.BOOK_PAGE_READY, this._pageReady);
    const book = BookStore.get(this.props.isbn);
    if (book.viewerIsReady) {
      book.viewer.Book.removeEnhancedFormattingCSS();
      book.viewer.Book.setTextSize(1);
    }
  }

  saveContentSettings() {
    Fraction.Helpers.saveUserData(
      `enhanced-content-settings:${this.props.isbn}`,
      this.state
    );
    this.applySettings();
  }

  loadContentSettings(isbn) {
    const hasFeatureFlag = BookStore.hasFeatureFlag(
      isbn,
      "cc_default_config_0"
    );
    const userData = Fraction.Helpers.getUserData(
      `enhanced-content-settings:${isbn}`
    );
    if (hasFeatureFlag) {
      return userData || cc_default_config_0;
    }
    return userData || controlDefaults;
  }

  applySettings() {
    const { font, lineHeight, margin, mode, textSize, enabled } = this.state;
    const book = BookStore.get(this.props.isbn);
    if (book.viewerIsReady && enabled) {
      // Seems that we have to reset the default settings...
      book.viewer.Book.addEnhancedFormattingCSS();
      if (font.enabled) {
        book.viewer.Book.setFontFamily(font.value);
      } else {
        book.viewer.Book.setFontFamily("default");
      }
      if (lineHeight.enabled) {
        book.viewer.Book.setLineHeight(lineHeight.value);
      } else {
        book.viewer.Book.setLineHeight("");
      }
      if (margin.enabled) {
        book.viewer.Book.setPageMargin(margin.value);
      } else {
        book.viewer.Book.setPageMargin("inherit");
      }
      if (mode.enabled) {
        book.viewer.Book.setDisplayMode(mode.value);
      } else {
        book.viewer.Book.setDisplayMode("");
      }
      if (textSize.enabled) {
        book.viewer.Book.setTextSize(textSize.value);
      } else {
        book.viewer.Book.setTextSize(1);
      }
    }
  }

  _pageReady() {
    this.applySettings();
  }

  _setMode(value) {
    const book = BookStore.get(this.props.isbn);
    book.viewer.Book.setDisplayMode(value);
    this.setState(
      { mode: { value, enabled: true }, enabled: true },
      this.saveContentSettings
    );
  }

  _setFont(e) {
    const value = e.target.options[e.target.selectedIndex].value;
    const book = BookStore.get(this.props.isbn);
    book.viewer.Book.setFontFamily(value);
    this.setState(
      { font: { value, enabled: true }, enabled: true },
      this.saveContentSettings
    );
  }

  _setPageMargin(value) {
    const book = BookStore.get(this.props.isbn);
    book.viewer.Book.setPageMargin(value);
    this.setState(
      { margin: { value, enabled: true }, enabled: true },
      this.saveContentSettings
    );
  }

  _setTextSize(value) {
    const book = BookStore.get(this.props.isbn);
    book.viewer.Book.setTextSize(value, () => {});
    this.setState(
      { textSize: { value, enabled: true }, enabled: true },
      this.saveContentSettings
    );
  }

  _setLineHeight(value) {
    const book = BookStore.get(this.props.isbn);
    book.viewer.Book.setLineHeight(value);
    this.setState(
      { lineHeight: { value, enabled: true }, enabled: true },
      this.saveContentSettings
    );
  }

  _resetFormatting() {
    const book = BookStore.get(this.props.isbn);
    const hasFeatureFlag = BookStore.hasFeatureFlag(
      this.props.isbn,
      "cc_default_config_0"
    );
    let controls = controlDefaults;
    if (hasFeatureFlag) {
      controls = cc_default_config_0;
    }
    this.setState({ ...controls }, () => {
      book.viewer.Book.setTextSize(1);
      book.viewer.Book.setDisplayMode("");
      book.viewer.Book.setPageMargin("inherit");
      book.viewer.Book.setLineHeight("");
      book.viewer.Book.setFontFamily("default");
      book.viewer.Book.removeEnhancedFormattingCSS();
      this.saveContentSettings();
      Dispatcher.emit(Dispatcher.OPEN_MODAL, "enhanced-formatting-feedback", {
        isbn: this.props.isbn
      });
    });
  }

  renderControls() {
    const { lineHeight, margin, mode, textSize, enabled } = this.state;

    const textSizeOptions = controls.textSizes.map(size => ({
      label: "Aa",
      ariaLabel: i18n.t(`enhanced-formatting.menu.text-sizes.${size.key}`),
      key: `Aa-${size.value}`,
      style: { fontSize: size.buttonFontSize },
      selected: size.value === textSize.value && textSize.enabled,
      value: size.value
    }));

    return (
      <div>
        <MenuHeader>
          {i18n.t("enhanced-formatting.menu.headers.text-size")}
        </MenuHeader>
        <div className="enhanced-formatting__menu-item">
          <ButtonGroup buttons={textSizeOptions} onChange={this._setTextSize} />
        </div>
        <MenuHeader>
          {i18n.t("enhanced-formatting.menu.headers.font")}
        </MenuHeader>
        <div className="enhanced-formatting__menu-item">
          <select
            id="enhanced-formatting-font"
            aria-label={i18n.t("enhanced-formatting.menu.headers.font")}
            onChange={this._setFont}
            value={this.state.font.value}
            className="vst-select"
          >
            {controls.fonts.map(fontItem => (
              <option value={fontItem.value} key={fontItem.key}>
                {i18n.t(`enhanced-formatting.menu.fonts.${fontItem.key}`)}
              </option>
            ))}
          </select>
        </div>
        <MenuHeader>
          {i18n.t("enhanced-formatting.menu.headers.mode")}
        </MenuHeader>
        <div className="enhanced-formatting__menu-item">
          <div className="enhanced-formatting__options enhanced-formatting__modes">
            {controls.modes.map(modeItem => (
              <div
                key={modeItem}
                className={classnames("enhanced-formatting__options-item", {
                  "--selected":
                    enabled && modeItem === mode.value && mode.enabled
                })}
              >
                <Button
                  className={`--mode-${modeItem}`}
                  label={i18n.t(`enhanced-formatting.menu.modes.${modeItem}`)}
                  onClick={() => this._setMode(modeItem)}
                >
                  {i18n.t(`enhanced-formatting.menu.modes.${modeItem}`)}
                </Button>
              </div>
            ))}
          </div>
        </div>
        <MenuHeader>
          {i18n.t("enhanced-formatting.menu.headers.margin")}
        </MenuHeader>
        <div className="enhanced-formatting__menu-item">
          <div className="enhanced-formatting__options">
            {controls.marginSizes.map(size => (
              <Button
                label={i18n.t(
                  `enhanced-formatting.menu.margin-sizes.${size.key}`
                )}
                className={classnames(
                  "enhanced-formatting__options-item no-button",
                  {
                    "--selected":
                      enabled && size.value === margin.value && margin.enabled
                  }
                )}
                key={size.key}
                naked
                inline
                onClick={() => this._setPageMargin(size.value)}
              >
                <Isvg src={`/img/page_margin_${size.key}.svg`} />
              </Button>
            ))}
          </div>
        </div>
        <MenuHeader>
          {i18n.t("enhanced-formatting.menu.headers.line-height")}
        </MenuHeader>
        <div className="enhanced-formatting__menu-item">
          <div className="enhanced-formatting__options">
            {controls.lineHeightSizes.map(size => (
              <Button
                label={i18n.t(
                  `enhanced-formatting.menu.line-height-sizes.${size.key}`
                )}
                className={classnames(
                  "enhanced-formatting__options-item no-button",
                  {
                    "--selected":
                      enabled &&
                      size.value === lineHeight.value &&
                      lineHeight.enabled
                  }
                )}
                naked
                inline
                key={size.key}
                onClick={() => this._setLineHeight(size.value)}
              >
                <Isvg src={`/img/line_height_${size.key}.svg`} />
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    // https://stackoverflow.com/questions/26633258/how-can-i-detect-css-variable-support-with-javascript#
    if (
      !(
        window.CSS &&
        window.CSS.supports &&
        window.CSS.supports("color", "var(--primary)")
      )
    ) {
      return (
        <div className="enhanced-formatting__menu-item">
          <div>{i18n.t("enhanced-formatting.browser-support")}</div>
        </div>
      );
    }

    return (
      <span>
        {this.renderControls()}
        <div className="enhanced-formatting__menu-item enhanced-formatting__toggle">
          <div>{i18n.t("enhanced-formatting.something-not-look-right")}</div>
          <Button inline size="small" onClick={this._resetFormatting}>
            {i18n.t("enhanced-formatting.reset-to-publisher")}
          </Button>
        </div>
      </span>
    );
  }
}

EnhancedFormattingMenu.propTypes = {
  isbn: PropTypes.string.isRequired
};

export default EnhancedFormattingMenu;
